import { useEffect, useMemo, useState } from 'react'
import { AreaDeTexto } from '@ui/primitivas/AreaDeTexto'
import { Boton } from '@ui/primitivas/Boton'
import { Campo } from '@ui/primitivas/Campo'
import { Selector } from '@ui/primitivas/Selector'
import { evaluacionPrevia, type EvaluacionPrevia } from './clienteDeMatrices'
import { CLASES_DE_NIVEL, ETIQUETA_DE_PRELACION } from './etiquetas'
import type { Catalogo, Fila, FilaEntrada, NivelDeRiesgo, Prelacion, Tarea } from './tipos'

export interface FormularioDeFilaProps {
  catalogo: Catalogo
  tareas: Tarea[]
  inicial: Fila | null
  tareaInicial?: string
  trabajando?: boolean
  error?: string | null
  alGuardar: (entrada: FilaEntrada) => void
  alCancelar: () => void
}

interface Estado {
  tarea_id: string
  peligro: string
  clasificacion_id: string
  familia_id: string
  riesgo_id: string
  justificacion: string
  probabilidad: string
  consecuencia: string
  magnitud_exposicion: string
  nivel_riesgo: string
  fuente: string
  medidas: { descripcion: string; prelacion: Prelacion }[]
}

function estadoInicial(p: FormularioDeFilaProps): Estado {
  const f = p.inicial
  const e = f?.evaluacion
  return {
    tarea_id: f?.tarea_id ?? p.tareaInicial ?? p.tareas[0]?.id ?? '',
    peligro: f?.peligro ?? '',
    clasificacion_id: f?.clasificacion_id ?? '',
    familia_id: f?.familia_id ?? '',
    riesgo_id: f?.riesgo_id ?? '',
    justificacion: f?.justificacion ?? '',
    probabilidad: e?.tipo === 'motor' && e.version_motor === p.catalogo.version_motor ? String(e.probabilidad) : '',
    consecuencia: e?.tipo === 'motor' && e.version_motor === p.catalogo.version_motor ? String(e.consecuencia) : '',
    magnitud_exposicion: e?.tipo === 'protocolo' ? (e.magnitud_exposicion ?? '') : '',
    nivel_riesgo: e?.tipo === 'protocolo' ? (e.nivel_riesgo ?? '') : '',
    fuente: e?.tipo === 'protocolo' ? (e.fuente ?? '') : '',
    medidas: f?.medidas.map((m) => ({ descripcion: m.descripcion, prelacion: m.prelacion })) ?? [],
  }
}

/** Editor completo de una fila. Siempre disponible: «siempre con el botón de editar». */
export function FormularioDeFila(props: FormularioDeFilaProps) {
  const [s, setS] = useState<Estado>(() => estadoInicial(props))
  const [previa, setPrevia] = useState<EvaluacionPrevia | null>(null)
  const [nuevaMedida, setNuevaMedida] = useState<{ descripcion: string; prelacion: Prelacion }>({ descripcion: '', prelacion: 'administrativa' })

  const clasificacion = props.catalogo.clasificaciones.find((c) => c.id === s.clasificacion_id)
  const familia = clasificacion?.familias.find((f) => f.id === s.familia_id)
  const porMotor = clasificacion?.por_motor ?? false
  const opcionesDeEscala = useMemo(
    () => ({
      probabilidad: props.catalogo.escalas.probabilidad.map((o) => ({ valor: String(o.valor), etiqueta: `${o.valor} · ${o.etiqueta}`, descripcion: o.descripcion })),
      consecuencia: props.catalogo.escalas.consecuencia.map((o) => ({ valor: String(o.valor), etiqueta: `${o.valor} · ${o.etiqueta}`, descripcion: o.descripcion })),
    }),
    [props.catalogo],
  )

  useEffect(() => {
    if (!porMotor || !s.probabilidad || !s.consecuencia) {
      setPrevia(null)
      return
    }
    let vigente = true
    evaluacionPrevia(Number(s.probabilidad), Number(s.consecuencia))
      .then((r) => {
        if (vigente) setPrevia(r)
      })
      .catch(() => {
        if (vigente) setPrevia(null)
      })
    return () => {
      vigente = false
    }
  }, [porMotor, s.probabilidad, s.consecuencia])

  const completa =
    s.tarea_id && s.peligro.trim() && s.riesgo_id && (!porMotor || (s.probabilidad && s.consecuencia)) && (porMotor || !s.magnitud_exposicion === !s.nivel_riesgo)

  const enviar = () => {
    if (!completa) return
    const base: FilaEntrada = {
      tarea_id: s.tarea_id,
      peligro: s.peligro.trim(),
      riesgo_id: s.riesgo_id,
      justificacion: s.justificacion.trim(),
      medidas: s.medidas,
    }
    if (porMotor) {
      props.alGuardar({ ...base, probabilidad: Number(s.probabilidad), consecuencia: Number(s.consecuencia) })
    } else {
      props.alGuardar({
        ...base,
        protocolo_sugerido: familia?.protocolo ?? null,
        magnitud_exposicion: s.magnitud_exposicion.trim() || null,
        nivel_riesgo: s.nivel_riesgo.trim() || null,
        fuente: s.fuente.trim() || null,
      })
    }
  }

  return (
    <form
      className="grid gap-3 rounded-lg border border-accion/60 bg-superficie p-3 shadow-2 sm:p-4"
      aria-label={props.inicial ? 'Editar la fila' : 'Nueva fila'}
      onSubmit={(e) => {
        e.preventDefault()
        enviar()
      }}
    >
      <p className="text-h3">{props.inicial ? 'Editar la fila' : 'Nueva fila'}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Selector etiqueta="Tarea" valor={s.tarea_id} opciones={props.tareas.map((t) => ({ valor: t.id, etiqueta: `${t.puesto}: ${t.tarea}` }))} alCambiar={(v) => setS({ ...s, tarea_id: v })} requerido />
        <Campo etiqueta="Identificación del peligro o factor de riesgo" valor={s.peligro} alCambiar={(v) => setS({ ...s, peligro: v })} requerido autoFoco />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Selector
          etiqueta="Clasificación"
          valor={s.clasificacion_id}
          marcador="Elige…"
          opciones={props.catalogo.clasificaciones.map((c) => ({ valor: c.id, etiqueta: c.etiqueta }))}
          alCambiar={(v) => setS({ ...s, clasificacion_id: v, familia_id: '', riesgo_id: '', probabilidad: '', consecuencia: '' })}
          requerido
        />
        <Selector
          etiqueta="Familia del riesgo"
          valor={s.familia_id}
          marcador="Elige…"
          opciones={(clasificacion?.familias ?? []).map((f) => ({ valor: f.id, etiqueta: f.etiqueta }))}
          alCambiar={(v) => setS({ ...s, familia_id: v, riesgo_id: '' })}
          requerido
        />
        <Selector
          etiqueta="Riesgo específico"
          valor={s.riesgo_id}
          marcador="Elige…"
          opciones={(familia?.riesgos ?? []).map((r) => ({ valor: r.id, etiqueta: r.etiqueta }))}
          alCambiar={(v) => setS({ ...s, riesgo_id: v })}
          requerido
        />
      </div>

      {clasificacion ? (
        porMotor ? (
          <fieldset className="grid gap-3 rounded-md border border-borde p-3">
            <legend className="sst-leyenda">Evaluación · VEP = P × C (guía ISP v3)</legend>
            {props.inicial?.version_motor !== props.catalogo.version_motor && props.inicial ? <p className="text-pequeno text-atencion-texto">Evaluación anterior: selecciona nuevamente P y C según la guía ISP antes de guardar.</p> : null}
            <div className="grid gap-3 md:grid-cols-2">
              <Selector etiqueta="Probabilidad (P)" valor={s.probabilidad} marcador="Elige…" opciones={opcionesDeEscala.probabilidad} alCambiar={(v) => setS({ ...s, probabilidad: v })} requerido compacto />
              <Selector etiqueta="Consecuencia (C)" valor={s.consecuencia} marcador="Elige…" opciones={opcionesDeEscala.consecuencia} alCambiar={(v) => setS({ ...s, consecuencia: v })} requerido compacto />
            </div>
            {previa ? (
              <p role="status" className={`rounded-md px-3 py-2 text-pequeno ${CLASES_DE_NIVEL[previa.nivel as NivelDeRiesgo] ?? ''}`}>
                VEP {previa.vep} · <strong>{previa.nivel_etiqueta}</strong>. {previa.accion}
              </p>
            ) : (
              <p className="text-pequeno text-texto-secundario">Elige P y C: considera la frecuencia de exposición al juzgar P. Atalaya calcula el VEP al instante.</p>
            )}
          </fieldset>
        ) : (
          <fieldset className="grid gap-3 rounded-md border border-borde p-3">
            <legend className="sst-leyenda">Evaluación por protocolo</legend>
            <p className="text-pequeno text-texto-secundario">
              Este riesgo no se evalúa con VEP. Protocolo de referencia: <strong>{familia?.protocolo ?? 'el aplicable según la normativa vigente'}</strong>. Registra el resultado del informe técnico cuando exista; hasta entonces queda «pendiente de protocolo». Atalaya nunca inventa una medición.
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <Campo etiqueta="Magnitud de la exposición" valor={s.magnitud_exposicion} alCambiar={(v) => setS({ ...s, magnitud_exposicion: v })} ayuda="P. ej. «Nivel 2», «< 25 % LPP»" />
              <Campo etiqueta="Nivel de riesgo (protocolo)" valor={s.nivel_riesgo} alCambiar={(v) => setS({ ...s, nivel_riesgo: v })} ayuda="P. ej. «Medio», «Categoría amarillo»" />
              <Campo etiqueta="Fuente" valor={s.fuente} alCambiar={(v) => setS({ ...s, fuente: v })} ayuda="Informe técnico y fecha" />
            </div>
          </fieldset>
        )
      ) : null}

      <AreaDeTexto etiqueta="Justificación" valor={s.justificacion} alCambiar={(v) => setS({ ...s, justificacion: v })} filas={2} ayuda="Por qué este peligro está presente en la tarea y qué sustenta la evaluación." />

      <fieldset className="grid gap-2 rounded-md border border-borde p-3">
        <legend className="sst-leyenda">Medidas de control (con prelación)</legend>
        {s.medidas.length ? (
          <ul className="grid gap-1 text-pequeno">
            {s.medidas.map((m, i) => (
              <li key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-borde px-2 py-1">
                <span>
                  <span className="rounded-pill bg-informativo-fondo px-2 text-etiqueta text-informativo-texto">{ETIQUETA_DE_PRELACION[m.prelacion]}</span> {m.descripcion}
                </span>
                <Boton variante="peligro" compacto etiquetaAccesible={`Quitar la medida ${i + 1}`} alPulsar={() => setS({ ...s, medidas: s.medidas.filter((_, j) => j !== i) })}>
                  Quitar
                </Boton>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-pequeno text-atencion-texto">Agrega al menos una medida; prefiere las de mayor jerarquía.</p>
        )}
        <div className="grid items-end gap-2 md:grid-cols-[2fr_1fr_auto]">
          <Campo etiqueta="Medida" valor={nuevaMedida.descripcion} alCambiar={(v) => setNuevaMedida({ ...nuevaMedida, descripcion: v })} />
          <Selector
            etiqueta="Prelación"
            valor={nuevaMedida.prelacion}
            opciones={props.catalogo.prelaciones.map((p) => ({ valor: p.id, etiqueta: `${p.orden}. ${p.etiqueta}` }))}
            alCambiar={(v) => setNuevaMedida({ ...nuevaMedida, prelacion: v as Prelacion })}
          />
          <Boton
            variante="secundario"
            deshabilitado={!nuevaMedida.descripcion.trim()}
            alPulsar={() => {
              setS({ ...s, medidas: [...s.medidas, { descripcion: nuevaMedida.descripcion.trim(), prelacion: nuevaMedida.prelacion }] })
              setNuevaMedida({ descripcion: '', prelacion: nuevaMedida.prelacion })
            }}
          >
            Agregar medida
          </Boton>
        </div>
      </fieldset>

      {props.error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{props.error}</p> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Boton variante="secundario" alPulsar={props.alCancelar}>Cancelar</Boton>
        <Boton tipo="submit" trabajando={props.trabajando ?? false} deshabilitado={!completa}>
          {props.inicial ? 'Guardar y dar visto bueno' : 'Agregar fila validada'}
        </Boton>
      </div>
    </form>
  )
}
